import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { RouterModule } from '@angular/router';

import {
  EventLandingPage,
  EventLandingPagePreviewMode,
} from '../../core/models/event-landing-page.model';
import { CxIconComponent } from '../icon/cx-icon.component';
import { BannerImageComponent } from '../banner-image/banner-image.component';

export interface EventLandingCountdown {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

@Component({
  selector: 'app-event-landing-banner',
  standalone: true,
  imports: [CommonModule, RouterModule, CxIconComponent, BannerImageComponent],
  templateUrl: './event-landing-banner.component.html',
  styleUrl: './event-landing-banner.component.scss',
})
export class EventLandingBannerComponent {
  @Input() page: EventLandingPage | null = null;
  @Input() previewMode: EventLandingPagePreviewMode = 'auto';
  @Input() countdown: EventLandingCountdown | null = null;
  @Input() ctaMode: 'preview' | 'guest' | 'authenticated' = 'preview';
  @Input() primaryLabel = 'RSVP';
  @Input() statusMessage = '';
  @Input() isWorking = false;
  @Input() primaryDisabled = false;
  @Input() primaryUnavailable = false;
  @Input() primaryUnavailableHint = '';
  @Input() showCancel = false;
  @Input() showJoinNotice = false;
  @Input() watchNotice = '';
  @Input() showSecondaryAction = false;
  @Input() secondaryLabel = '';
  @Input() showFramingAction = false;
  @Output() adjustFraming = new EventEmitter<void>();
  @Output() share = new EventEmitter<void>();
  @Output() primaryAction = new EventEmitter<void>();
  @Output() cancelAction = new EventEmitter<void>();
  @Output() secondaryAction = new EventEmitter<void>();

  get displayModeClass(): string {
    return `event-landing-banner--${this.previewMode}`;
  }

  isTerminalStatus(page: EventLandingPage): boolean {
    return page.status === 'ENDED' || page.status === 'CANCELLED';
  }

  statusLabel(page: EventLandingPage): string {
    switch (page.status) {
      case 'LIVE':
        return 'Live now';
      case 'ENDED':
        return 'Event ended';
      case 'CANCELLED':
        return 'Event cancelled';
      default:
        return 'Upcoming live event';
    }
  }

  archiveMessage(page: EventLandingPage): string {
    return page.status === 'CANCELLED'
      ? 'This event was cancelled. Its details remain available for reference.'
      : 'This event has ended. Its details remain available in this archive.';
  }

  hasSchedule(page: EventLandingPage): boolean {
    return (
      Number.isFinite(Date.parse(page.startsAt)) &&
      Boolean(page.timezoneLabel && page.timezoneId)
    );
  }

  formatEventTime(page: EventLandingPage): string {
    try {
      return new Intl.DateTimeFormat('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZone: page.timezoneId,
        timeZoneName: 'short',
      }).format(new Date(page.startsAt));
    } catch {
      return page.startsAt;
    }
  }

  emitShare(): void {
    if (this.ctaMode !== 'preview') {
      this.share.emit();
    }
  }

  emitPrimaryAction(): void {
    if (this.ctaMode !== 'preview' && !this.isWorking) {
      this.primaryAction.emit();
    }
  }
}
